
import { OperatingHour } from "@/types/service";

/**
 * Filter time slots based on slot size
 * @param timeSlots - Array of time slots
 * @param slotSize - Desired slot size (30min, 60min, or allDay)
 * @returns Filtered array of time slots
 */
export const filterSlotsBySize = (
  timeSlots: string[],
  slotSize: "30" | "60" | "allDay"
): string[] => {
  if (slotSize === "allDay") {
    return ["09:00"]; // Just return a single start time for all-day events
  }

  if (slotSize === "60") {
    // For 60-minute slots, only include slots at the top of each hour
    return timeSlots.filter(slot => {
      const [_, minutes] = slot.split(':').map(Number);
      return minutes === 0;
    });
  }

  // For 30-minute slots, return all slots
  return timeSlots;
};

/**
 * Check if a time slot falls within operating hours for the given date
 * @param slot - Time slot in format "HH:MM"
 * @param date - The date to check
 * @param operatingHours - Array of operating hour configurations
 * @returns Boolean indicating if the slot is within operating hours
 */
export const isSlotWithinOperatingHours = (
  slot: string,
  date: Date,
  operatingHours: any[]
): boolean => {
  if (!operatingHours || operatingHours.length === 0) {
    return true; // If no operating hours defined, assume always available
  }

  const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, ...
  const [hours, minutes] = slot.split(':').map(Number);
  
  // Find operating hours for the current day of week
  const todaysHours = operatingHours.find(hour => 
    hour.daysOfWeek.includes(dayOfWeek)
  );
  
  // If no hours defined for this day, the service is closed
  if (!todaysHours) {
    return false;
  }
  
  // Parse start and end times
  const [startHours, startMinutes] = todaysHours.startTime.split(':').map(Number);
  const [endHours, endMinutes] = todaysHours.endTime.split(':').map(Number);
  
  // Convert all times to minutes for easier comparison
  const slotTimeInMinutes = hours * 60 + minutes;
  const startTimeInMinutes = startHours * 60 + startMinutes;
  const endTimeInMinutes = endHours * 60 + endMinutes;
  
  // Check if slot is within operating hours
  return slotTimeInMinutes >= startTimeInMinutes && slotTimeInMinutes < endTimeInMinutes;
};

/**
 * Filter time slots based on operating hours
 * @param timeSlots - Array of time slots
 * @param operatingHours - Array of operating hour configurations
 * @param date - The date to check against (defaults to current date)
 * @returns Filtered array of time slots within operating hours
 */
export const filterByOperatingHours = (
  timeSlots: string[],
  operatingHours: any[],
  date: Date = new Date()
): string[] => {
  if (!operatingHours || operatingHours.length === 0) {
    return timeSlots; // Return all slots if no operating hours defined
  }
  
  return timeSlots.filter(slot => 
    isSlotWithinOperatingHours(slot, date, operatingHours)
  );
};

/**
 * Get slot date-time by combining date and slot time
 * @param slot - Time slot in format "HH:MM"
 * @param date - Base date
 * @returns Date object representing the slot date-time
 */
export const getSlotDateTime = (slot: string, date: Date): Date => {
  const [hours, minutes] = slot.split(':').map(Number);
  // Create timezone-neutral Date object to avoid UTC conversion
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes, 0, 0);
};
